import React from 'react';
import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import {
  BLOCKS,
  MARKS,
  Document as ContentfulDocument,
  Node,
  INLINES,
} from '@contentful/rich-text-types';
import parse from 'html-react-parser';

import { ContentfulRendererProps, TextNodeTypes } from './ContentfulRenderer.types';
import { renderUnorderedListItems, parseTinyMceWithVariants } from './listVariants';

import InlineSVG from '../InlineSVG';
import NextImage from '../NextImage';
import RenderBodyCopy from '../RenderBodyCopy';
import Button from '../Button';
import TablePrimary from '../TablePrimary';
import ContentfulTextEditor from '../ContentfulTextEditor';
import ScrollToWrapper from '../ScrollToWrapper';
import NextLink from '../NextLink';
import SocialEmbeds from '../SocialEmbeds/SocialEmbeds';
import Caption from '../Caption/Caption';

import isEmpty from '@src/utils/isEmpty';
import isFullUrl from '@src/utils/isFullUrl';
import determineMediaType from '@src/utils/determineMediaType';

const ContentfulRenderer: React.FC<ContentfulRendererProps> = ({
  document,
  links,
  inspectorProps,
}) => {
  const options: Options = {
    renderMark: {
      [MARKS.BOLD]: (_node: React.ReactNode): React.ReactNode => (
        <strong className='text-primary font-bold'>{_node}</strong>
      ),
      [MARKS.ITALIC]: (_node: React.ReactNode): React.ReactNode => (
        <em className='italic'>{_node}</em>
      ),
      [MARKS.UNDERLINE]: (_node: React.ReactNode): React.ReactNode => <u>{_node}</u>,
      [MARKS.CODE]: (text: React.ReactNode): React.ReactNode => {
        // Check if text is a string, as parse function expects a string
        if (typeof text === 'string') {
          return parse(text);
        }
        // Handle other types or return text as is
        return text;
      },
      [MARKS.SUPERSCRIPT]: (_node: React.ReactNode): React.ReactNode => <sup>{_node}</sup>,
      [MARKS.SUBSCRIPT]: (_node: React.ReactNode): React.ReactNode => <sub>{_node}</sub>,
    },

    renderNode: {
      [BLOCKS.HEADING_2]: (_node, children: React.ReactNode) => {
        const textNode = _node?.content?.[0] as TextNodeTypes;
        const id = textNode?.value
          ?.trim()
          .replace(/[^a-zA-Z0-9\s]/g, '')
          .replace(/\s+/g, '-')
          .toLowerCase();

        return (
          <h2
            data-id={id}
            className='text-body-copy--dark 3xl:text-[3.2rem] 3xl:leading-[3.8rem] mt-[1.6rem] inline-block text-[1.8rem] leading-[2.5rem] font-medium text-pretty md:text-[2.4rem] md:leading-[3.2rem]'
          >
            {children}
          </h2>
        );
      },
      [BLOCKS.HEADING_3]: (_node, children: React.ReactNode) => {
        const textNode = _node?.content?.[0] as TextNodeTypes;
        const id = textNode?.value
          ?.trim()
          .replace(/[^a-zA-Z0-9\s]/g, '')
          .replace(/\s+/g, '-')
          .toLowerCase();

        return (
          <h3
            data-id={id}
            className='text-body-copy--dark inline-block text-[1.6rem] leading-[2rem] font-medium text-pretty md:text-[1.85rem] md:leading-[3rem]'
          >
            {children}
          </h3>
        );
      },
      [BLOCKS.PARAGRAPH]: (_node: Node, children: React.ReactNode) => {
        return (
          <p className='text-body-copy--dark inline-block w-full text-[1.6rem] leading-[2.56rem] font-light text-pretty md:text-[1.8rem] md:leading-[2.88rem]'>
            {children}
          </p>
        );
      },
      [BLOCKS.EMBEDDED_ASSET]: (_node) => {
        const assetId = _node?.data?.target?.sys?.id;
        const asset = links?.assets?.block.find((asset) => asset?.sys?.id === assetId);

        if (
          asset?.contentType === 'image/webp' ||
          asset?.contentType === 'image/jpeg' ||
          asset?.contentType === 'image/png' ||
          asset?.contentType === 'image/gif'
        ) {
          return (
            <NextImage
              className='my-[1rem]'
              src={asset?.url}
              aspectRatio={asset?.width / asset?.height}
              width={asset?.width}
              alt={asset?.description}
              caption={asset?.description}
            />
          );
        }

        if (asset?.contentType === 'image/svg+xml') {
          return (
            <InlineSVG
              src={asset?.url}
              caption={asset?.description}
              renderAt={['desktop', 'tablet', 'mobile']}
            />
          );
        }

        if (
          asset?.contentType?.includes('video/mp4') ||
          asset?.contentType?.includes('video/x-m4v') ||
          asset?.contentType?.includes('video/quicktime')
        ) {
          return <SocialEmbeds data={{ ...asset, platform: 'Contentful', embedUrl: asset?.url }} />;
        }
        return null;
      },
      [BLOCKS.EMBEDDED_ENTRY]: (_node) => {
        const entryId = _node?.data?.target?.sys?.id;
        const entry = links?.entries?.block.find((entry: any) => entry?.sys?.id === entryId);

        if (entry?.__typename === 'Image') {
          // Object map for alignment styles
          const alignmentStyles: any = {
            Left: { marginLeft: '0', marginRight: 'auto' },
            Right: { marginLeft: 'auto', marginRight: '0' },
            Center: { margin: '0 auto' },
          };

          // Get the alignment style from the map, default to an empty object if not found
          const alignmentStyle = alignmentStyles[entry?.alignment] || {};

          const imageElement = (
            <div className='mb-[1.2rem]'>
              {determineMediaType({ url: entry?.media?.url }) === 'image' ? (
                <NextImage
                  style={{ width: `${entry?.width}%`, ...alignmentStyle }}
                  objectFit='contain'
                  imgClassName={`${entry?.boxShadow === 'Yes' ? 'box-shadow' : ''}`}
                  src={entry?.media?.url}
                  alt={entry?.media?.description}
                  aspectRatio={entry?.media?.width / entry?.media?.height}
                  width={entry?.media?.width}
                />
              ) : null}

              {determineMediaType({ url: entry?.media?.url }) === 'svg' ? (
                <InlineSVG
                  style={{ width: `${entry?.width}%`, ...alignmentStyle }}
                  src={entry?.media?.url}
                  className={`${entry?.boxShadow === 'Yes' ? 'box-shadow' : ''}`}
                  renderAt={['desktop', 'tablet', 'mobile']}
                />
              ) : null}

              {entry?.bodyCopy && <Caption bodyCopy={entry?.bodyCopy} />}
            </div>
          );

          if (!isEmpty({ value: entry?.url })) {
            if (!isFullUrl({ url: entry?.url })) {
              if (entry?.url?.startsWith('#')) {
                return <ScrollToWrapper children={imageElement} href={entry?.url} />;
              }

              return (
                <NextLink className='inline-block w-full' href={`${entry?.url}`}>
                  {imageElement}
                </NextLink>
              );
            } else {
              return (
                <a
                  href={entry?.url}
                  className='inline-block w-full'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {imageElement}
                </a>
              );
            }
          }

          return imageElement;
        }

        if (entry?.__typename === 'TextBox') {
          const colors: any = {
            Orange: { primary: '#f16e00', secondary: '#FFD9B7' },
            Grey: { primary: '#E5E7EB', secondary: '#FAFAFA' },
          };

          return (
            <div
              style={{
                borderColor: colors?.[entry?.color]?.primary,
                backgroundColor: colors?.[entry?.color]?.secondary,
              }}
              className={`rounded-2xl border-[0.3rem] border-l-[2rem] p-[1rem] text-left`}
            >
              <RenderBodyCopy
                bodyCopy={entry?.bodyCopy?.json?.content}
                entries={entry?.bodyCopy?.links?.entries?.block}
                assets={entry.bodyCopy.links.assets.block}
                className='text-body-copy--dark hover:[&>a]:text-primary--v2 inline-block w-full text-[1.8rem] leading-[2.56rem] font-light md:text-[1.8rem] md:leading-[2.88rem] [&>a]:text-[1.8rem] [&>a]:font-normal [&>a]:underline'
              />
            </div>
          );
        }

        if (entry?.__typename === 'DuplexComponent') return null;

        if (entry?.__typename === 'Button') {
          return (
            <div className=''>
              <Button primary href={entry?.url} btnText={entry?.text} target={entry?.target} />
            </div>
          );
        }

        if (entry.__typename === 'TextEditor' && !isEmpty({ value: entry?.tinyMce })) {
          return (
            <div>
              <ContentfulTextEditor>
                {parseTinyMceWithVariants(entry?.tinyMce)}
              </ContentfulTextEditor>
              <Caption bodyCopy={entry?.bodyCopy} />
            </div>
          );
        }

        if (entry?.__typename === 'Grid') return null;

        if (entry?.__typename === 'SocialEmbed') {
          return <SocialEmbeds data={entry} />;
        }

        return null;
      },
      [BLOCKS.UL_LIST]: (_node, children) => {
        return (
          <ul className='text-body-copy--dark marker:text-body-copy--dark relative left-[-0.3rem] list-disc pl-[2.4rem] text-[1.6rem] leading-[2.5rem] font-light text-pretty md:text-[1.8rem] md:leading-[2.88rem] [&>li:not(:last-child)]:mb-[2rem] [&>li>ul]:list-[circle] [&>li>ul>li:first-child]:mt-[2rem]'>
            {renderUnorderedListItems(_node, children)}
          </ul>
        );
      },
      [BLOCKS.OL_LIST]: (_node, children) => {
        return (
          <ol className='nested text-body-copy--dark marker:text-body-copy--dark relative text-[1.6rem] leading-[2.5rem] font-light text-pretty md:text-[1.8rem] md:leading-[2.88rem] [&>li:not(:last-child)]:mb-[2rem]'>
            {children}
          </ol>
        );
      },
      [BLOCKS.LIST_ITEM]: (_node, children) => {
        const normalized: React.ReactNode[] = React?.Children?.toArray(children)?.flatMap(
          (node) => {
            if (React?.isValidElement<Record<string, any>>(node) && node?.type === 'p') {
              return React?.Children?.toArray(node?.props?.children);
            }
            return node;
          }
        );

        return <li>{normalized}</li>;
      },
      [INLINES.HYPERLINK]: (_node, children) => {
        const href = _node?.data?.uri;
        const target = isFullUrl({ url: href }) ? '_blank' : '_self';

        if (href?.startsWith('#')) {
          return <ScrollToWrapper children={children} href={href} />;
        }

        return (
          <a
            href={href}
            target={target}
            rel='noopener noreferrer'
            className='hover:text-primary--v2 font-normal underline'
          >
            {children}
          </a>
        );
      },
      [BLOCKS.QUOTE]: (_node) => {
        const content = documentToReactComponents(_node as ContentfulDocument, {
          renderNode: {
            [BLOCKS.PARAGRAPH]: (_node, children) => children,
            [BLOCKS.QUOTE]: (_node, children) => children,
          },
          renderMark: {
            [MARKS.CODE]: (_node: React.ReactNode) => <span>{parse(`${_node} `)}</span>,
            [MARKS.BOLD]: (_node: React.ReactNode): React.ReactNode => (
              <strong className='text-primary font-medium'>{_node}</strong>
            ),
            [MARKS.SUPERSCRIPT]: (_node: React.ReactNode): React.ReactNode => <sup>{_node}</sup>,
            [MARKS.SUBSCRIPT]: (_node: React.ReactNode): React.ReactNode => <sub>{_node}</sub>,
            [MARKS.ITALIC]: (_node: React.ReactNode): React.ReactNode => <em>{_node}</em>,
          },
        });

        return (
          <blockquote className='border-primary text-body-copy my-[1rem] border-l-[0.5rem] pl-[1.6rem] text-[1.6rem] leading-[2.4rem] font-normal text-pretty text-[500] md:text-[2.4rem] md:leading-[3.6rem] [&>a]:font-normal [&>a]:underline!'>
            {content}
          </blockquote>
        );
      },
      [BLOCKS.HR]: () => <hr className='border-border'></hr>,
      [BLOCKS.TABLE]: (_node) => {
        const content = documentToReactComponents(_node as ContentfulDocument, {
          renderNode: {
            [BLOCKS.TABLE]: (_node, children: any) => {
              const headerRow = children?.[0];
              const bodyRows = children?.slice(1);

              const checkMergedRow = (element: any) => {
                if (element?.type === 'span' && element?.props?.children?.slice(0, 3) === 'row') {
                  return true;
                }
                // If the current element has children, recursively check each child
                if (element?.props && Array.isArray(element?.props?.children)) {
                  for (const child of element.props.children) {
                    if (typeof child === 'object' && checkMergedRow(child)) {
                      return true;
                    }
                  }
                }
                // If the current element is an array, recursively check each element
                if (Array.isArray(element)) {
                  for (const item of element) {
                    if (checkMergedRow(item)) {
                      return true;
                    }
                  }
                }
                // No matching element found
                return false;
              };

              const mergedRow = checkMergedRow(children);

              return (
                <TablePrimary
                  headerRow={headerRow}
                  bodyRows={bodyRows}
                  className={` ${mergedRow ? '[&>tbody>tr]:bg-background-secondary' : '[&>tbody>tr]:odd:bg-background-grey-light [&>tbody>tr]:even:bg-background-secondary'}`}
                />
              );
            },
            [BLOCKS.TABLE_HEADER_CELL]: (_node, children) => (
              <td className='text-body-copy--light bg-[#ff7a01] px-[1rem] py-[1rem] align-top text-2xl text-[1.6rem] leading-normal font-medium [&>a]:font-light [&>a]:underline'>
                {children}
              </td>
            ),
            [BLOCKS.TABLE_ROW]: (_node, children) => {
              return <tr className='text-body-copy font-normal'>{children}</tr>;
            },

            [BLOCKS.TABLE_CELL]: (_node, children) => {
              const merge = (children as any)?.[0]?.[1]?.props?.children;
              const mergeType = merge !== 'hidden' && merge?.slice(0, 3);
              const mergeQuantity = merge !== 'hidden' && merge?.slice(4, 5);

              const processChildren = (child: React.ReactNode): React.ReactNode => {
                if (
                  typeof child === 'string' ||
                  typeof child === 'number' ||
                  typeof child === 'boolean' ||
                  !child
                ) {
                  return child;
                }

                if (!React.isValidElement<Record<string, any>>(child)) {
                  return child;
                }

                if (child.type === 'a') {
                  const { href, children: linkChildren, ...props } = child.props as any;

                  return (
                    <NextLink
                      href={href || '#'}
                      {...props}
                      target={href?.startsWith('http') ? '_blank' : undefined}
                      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className={`${props.className || ''} font-light underline`}
                    >
                      {linkChildren}
                    </NextLink>
                  );
                }

                if (child.props.children) {
                  return React.cloneElement(child, {
                    ...child.props,
                    children: React.Children.map(child.props.children, processChildren),
                  });
                }

                return child;
              };

              return (
                <td
                  colSpan={mergeType === 'col' ? mergeQuantity : 1}
                  rowSpan={mergeType === 'row' ? mergeQuantity : 1}
                  className={`text-body-copy content-center p-[1rem] align-top text-[1.6rem] leading-normal font-extralight [&>a]:font-light [&>a]:underline ${merge === 'hidden' ? 'hidden' : ''}`}
                >
                  {React.Children.map(children, processChildren)}
                </td>
              );
            },
            [BLOCKS.PARAGRAPH]: (_node, children) => {
              return children;
            },
            [INLINES.EMBEDDED_ENTRY]: (_node) => {
              const entryId = _node?.data?.target?.sys?.id;
              const entry = links?.entries?.inline.find((entry) => entry?.sys?.id === entryId);

              if (entry?.__typename === 'Image') {
                return (
                  <NextImage
                    className='mx-auto my-[1rem] overflow-hidden'
                    style={{ width: `${entry?.width}%` }}
                    src={entry?.media?.url}
                    aspectRatio={entry?.media?.width / entry?.media?.height}
                    width={entry?.media?.width}
                    alt={entry?.media?.description}
                    caption={entry?.media?.description}
                  />
                );
              }

              return null;
            },
          },
        });
        return <>{content}</>;
      },
      // Add other block types with their corresponding types...
    },
  };

  return (
    <div className='flex flex-col gap-[2.4rem]' {...inspectorProps}>
      {documentToReactComponents(document as ContentfulDocument, options)}
    </div>
  );
};

export default ContentfulRenderer;
